<template>
    <StfRow class="my-2">
        <StfCol>
            <StfAutoComplete
                label="Search and select approved descriptions"
                v-model="search"
                :options="autocompleteOptions"
                placeholder="Describe your product or service"
                @update:selected="onOptionSelect"              
                open-on-focus
            >
                <template #happen>
                    <StfButton color="primary" icon="magnifyingGlass" @click="doSearch(search)" :loading="loading" fill-height></StfButton>
                </template>
            </StfAutoComplete>
        </StfCol>
    </StfRow>

    <StfAlert :modelValue="noResultSearch" type="error" class="my-2">
        <template #icon>
            <StfIcon icon="lightbulb" size="small" color="black" />
        </template>
        <b>No relevant search results located.</b>  Please search for another term
    </StfAlert>
</template>

<script setup lang="ts">
import {ref, watch} from 'vue';
import { searchProduct } from '@/components/goods-view/searchProduct';
import useCountry from '@/composables/countryDependable';
import {useApplicationStore} from '@/store/application';
import useCurrentSearch from '@/components/goods-view/searchDataStore';
import {useProductStore} from '@/store/good';
import {storeToRefs} from 'pinia';
import { staticProductOptions } from '@/components/goods-view/staticProductOptions';
import type { DropdownItem, DropdownOption } from '@/ui/dropdown/StfDropdown';
import { useNotification } from '@/composables/useNotification';

const country = useCountry();
const {totalGoods, classes} = storeToRefs(useProductStore())
const {setNewSearch, haveSearched, noResultSearch} = useCurrentSearch();
const applicationStore = useApplicationStore();
const search = ref('');
const loading = ref(false);
const autocompleteOptions = ref<DropdownItem[]>([]);
const { showInfoNotification } = useNotification();

const emit = defineEmits(['loading-change']);

const doSearch = async (searchTerm: string) => {        
    loading.value = true;

    const response = await searchProduct(searchTerm, applicationStore.appReference, country.displayCode);

    if (response !== null) {
        setNewSearch(response);
    }

    loading.value = false;
}

const onOptionSelect = (value: string) => {
    // Find the selected option to get its label
    const selectedOption = autocompleteOptions.value.find((option): option is DropdownItem => 
        'value' in option && option.value === value
    );
    
    if (selectedOption) {
        search.value = (selectedOption as DropdownOption).label;
        doSearch((selectedOption as DropdownOption).label);
    }
}

watch(search, () => {
    setOptions();
});

watch(loading, (newValue) => {
    emit('loading-change', newValue);
})

const setOptions = () => {
    const options = [];
    // Add custom search option if user has typed something
    if (search.value.trim().length > 0) {
        options.push({
            label: search.value,
            value: search.value,
            icon: 'magnifyingGlass'
        });
        options.push({ type: 'separator' });
    }
    
    // Add static options
    staticProductOptions.forEach(option => {
        options.push({
            label: option.label,
            value: option.value,
            icon: option.icon
        });
    });

    autocompleteOptions.value = options as DropdownItem[];
};


watch(haveSearched, (newValue) => {
    if (newValue && totalGoods.value == 0) {
        showInfoNotification('<div><b>The most relevant TM Office approved products/services are below.</b> Select each of the items you want to protect your trademark for.</div><div class="mt-2">If the results don\'t appear relevant or do not cover what you need, try another search - adding more detail can help.</div>')
    }
})

watch(totalGoods, (newValue) => {
    if (newValue == 1 && haveSearched.value) {
        showInfoNotification(`<b>Great! You have selected your first item in Class ${classes.value[0]}.</b> You can add other items from the same class for free.`);
    }
})

setOptions();

</script>
